// ---------------------------------------------------------------------------
// Per-block optimistic-concurrency baselines (F11)
//
// Maps a studio_blocks DB id → the `updated_at` token the client last
// observed for that row. The token is OPAQUE: it is captured from server
// responses (GET /full rows, PATCH response bodies) and echoed back verbatim
// as an `If-Match` header — never Date-parsed or reformatted, because the
// server-side CAS compares it byte-for-byte (`updated_at=eq.<token>` against
// PostgREST's microsecond-precision timestamptz serialization).
//
// Lifecycle (must mirror every blockIdMap rebuild — stale tokens after id
// churn or row replacement would make every subsequent save spuriously 409):
//  - loadProject seeds the project's baselines from the /full rows
//    (replaceProjectBlockBaselines — feature-detect: rows without updated_at
//    simply get no baseline, so If-Match is never sent for them);
//  - every successful saveBlock PATCH whose response carries updated_at
//    refreshes that block's baseline (refreshBlockBaseline);
//  - persistGeneratedBlocks (PUT whole-replace; reassigns row ids AND resets
//    updated_at server-side) drops the project's baselines wholesale
//    (dropProjectBlockBaselines) — the PUT response carries no tokens, so
//    saves fall back to opportunistic no-If-Match until the next load/PATCH;
//  - closeProject drops the closed project's baselines AFTER its close-flush
//    settles (dropProjectBlockBaselines deferred — PI-L2-06: the flush reads
//    its If-Match tokens at dispatch time, so a synchronous clear stripped
//    every close-time PATCH of its token), unless the same project was
//    re-opened meanwhile (the load path then owns the fresh tokens).
//
// Module-level (not store state) on purpose: the consumers span services
// (project-api), the auto-save queue, and the bridge — a leaf module avoids
// the studio-store circular-import dance auto-save.ts documents.
// ---------------------------------------------------------------------------

interface BaselineEntry {
  projectId: string
  token: string
  /** CS-5 — the drop/replace generation in effect when this token was
   * stored. A refresh whose generation is older than the latest drop for the
   * block is a late straggler that must NOT resurrect a pre-drop token. */
  generation: number
}

const baselines = new Map<string, BaselineEntry>()

/** CS-5 — monotonic generation counter, bumped on every wholesale or
 * single-block DROP (the PUT whole-replace lane, project reload, close,
 * single-block LWW drop). saveBlock snapshots it BEFORE issuing the PATCH and
 * passes the snapshot into refreshBlockBaseline; if a drop bumped the counter
 * while that PATCH was on the wire, the response's token is pre-drop and the
 * refresh is ignored — otherwise a saveBlock 2xx settling AFTER
 * persistGeneratedBlocks' wholesale drop would re-insert the pre-PUT token,
 * making the next save spuriously 409. */
let dropGeneration = 0

/** CS-5 — read the live drop generation. saveBlock snapshots this at dispatch
 * time so refreshBlockBaseline can detect a drop that landed mid-flight. */
export function getBaselineGeneration(): number {
  return dropGeneration
}

/** RFC 7230 field-value bytes: VCHAR (0x21-0x7E) plus SP/HTAB. CS-7 — an
 * updated_at carrying CRLF / control bytes would make `new Headers({'If-Match':
 * token})` throw a synchronous TypeError, converting the bad token into a
 * permanent save lockout. Reject such tokens at the registry boundary so they
 * never reach a header. Exported so the saveBlock header-build site can apply
 * the identical guard (defense in depth). */
export function isHeaderSafeToken(token: string): boolean {
  // eslint-disable-next-line no-control-regex
  return /^[\t\x20-\x7e]*$/.test(token)
}

/** The last-observed updated_at token for a block, or null when the client
 * has never seen one (If-Match must then be omitted — opportunistic CAS). */
export function getBlockBaseline(blockId: string): string | null {
  return baselines.get(blockId)?.token ?? null
}

/** Record the token a successful server response reported for a block.
 * Drops (rather than stores) a token that is unsafe as an HTTP header value
 * (CS-7). When `expectedGeneration` is supplied (saveBlock snapshots it before
 * issuing the PATCH), a refresh whose generation no longer matches the live
 * one — i.e. a drop landed while the PATCH was on the wire — is ignored (CS-5)
 * so a late 2xx cannot resurrect a token a wholesale drop already invalidated. */
export function refreshBlockBaseline(
  projectId: string,
  blockId: string,
  token: string,
  expectedGeneration?: number,
): void {
  // CS-5 — a drop landed between dispatch and this refresh: the token is
  // pre-drop and stale. Ignore it (do NOT delete an entry a newer write may
  // legitimately hold; just decline to resurrect the old one).
  if (expectedGeneration !== undefined && expectedGeneration !== dropGeneration) {
    return
  }
  // CS-7 — never store a token that cannot be echoed into an If-Match header.
  // A malformed/compromised server string is treated as "no trustworthy
  // token": clear any prior entry so the next save falls back to LWW rather
  // than throwing on every attempt (permanent unsaveable block).
  if (!isHeaderSafeToken(token)) {
    baselines.delete(blockId)
    return
  }
  baselines.set(blockId, { projectId, token, generation: dropGeneration })
}

/** Wholesale-rebuild one project's baselines (load path): every previous
 * entry for the project is dropped, then `entries` (blockId → token) seed
 * the fresh set. Entries for OTHER projects are left untouched — pending
 * saves captured for a previously open project keep their tokens. */
export function replaceProjectBlockBaselines(
  projectId: string,
  entries: Record<string, string>,
): void {
  dropProjectBlockBaselines(projectId)
  for (const [blockId, token] of Object.entries(entries)) {
    if (token && isHeaderSafeToken(token)) {
      baselines.set(blockId, { projectId, token, generation: dropGeneration })
    }
  }
}

/** Drop a SINGLE block's baseline (L2-COR-07: a 2xx LWW response that
 * withholds updated_at can no longer be trusted — the row's server token has
 * advanced past the load-time one this client holds). Bumps the drop
 * generation so a concurrent refresh of the same block cannot resurrect it. */
export function dropBlockBaseline(blockId: string): void {
  dropGeneration += 1
  baselines.delete(blockId)
}

/** Drop every baseline belonging to a project — used after the PUT
 * whole-replace lane (persistGeneratedBlocks) invalidates all of them. */
export function dropProjectBlockBaselines(projectId: string): void {
  // CS-5 — bump the generation so a saveBlock 2xx that settles after this
  // drop (a same-project PATCH whose response races the PUT) cannot re-insert
  // its pre-drop token via refreshBlockBaseline.
  dropGeneration += 1
  for (const [blockId, entry] of baselines) {
    if (entry.projectId === projectId) baselines.delete(blockId)
  }
}

/** Drop everything (workspace reset / test isolation — the project-close
 * path uses the deferred, project-scoped drop above instead). */
export function clearBlockBaselines(): void {
  dropGeneration += 1
  baselines.clear()
}
